import { useEffect, useRef } from "react";
import { useBookingFlowStore } from "@/stores/bookingFlowStore";
import { getStepFromUrl, replaceStepInUrl } from "@/utils/urlUtils";
import type { BookingStep } from "@/types/booking";

/**
 * Hook to sync booking steps with browser history.
 * - Listens to popstate events (browser back/forward)
 * - Initializes step from URL on mount (only after session loading completes)
 * - Updates URL when step changes
 * - Syncs initial state to server on mount
 */
export const useStepHistory = () => {
  const currentStep = useBookingFlowStore((state) => state.currentStep);
  const isLoading = useBookingFlowStore((state) => state.isLoading);
  const hasInitializedFromUrl = useBookingFlowStore(
    (state) => state.hasInitializedFromUrl,
  );
  const setStepFromHistory = useBookingFlowStore(
    (state) => state.setStepFromHistory,
  );
  const syncSession = useBookingFlowStore((state) => state.syncSession);
  const hasInitialized = useRef(false);

  // Initialize step from URL on mount - but wait for session loading to complete
  // This prevents race condition where we try to validate step requirements
  // before the session state (products, clinic, etc.) is loaded from server
  useEffect(() => {
    // Skip if already initialized or still loading session
    if (hasInitialized.current || isLoading) {
      return;
    }

    hasInitialized.current = true;

    // If session was already loaded (e.g., from loadSession), don't override
    // the step - loadSession already set the correct step from server
    if (hasInitializedFromUrl) {
      console.log(
        "[useStepHistory] Session already initialized, skipping URL step init",
      );
      // Just sync to server, don't change step
      syncSession("initialLoad");
      return;
    }

    const stepFromUrl = getStepFromUrl();
    if (stepFromUrl && stepFromUrl !== currentStep) {
      console.log("[useStepHistory] Initializing step from URL:", stepFromUrl);
      setStepFromHistory(stepFromUrl as BookingStep);
    } else if (!stepFromUrl) {
      // No step in URL, set current step in URL (replace, not push)
      replaceStepInUrl(currentStep);
    }

    // Sync initial state to server
    syncSession("initialLoad");
  }, [isLoading, hasInitializedFromUrl]); // Re-run when loading state changes

  // Listen to popstate events (browser back/forward buttons)
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      console.log("[useStepHistory] popstate event:", event.state);

      // Try to get step from event state first, then from URL
      const step = event.state?.step || getStepFromUrl();

      if (step && step !== currentStep) {
        console.log("[useStepHistory] Navigating to step from history:", step);
        setStepFromHistory(step as BookingStep);
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [currentStep, setStepFromHistory]);
};
